import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const WorkPreferences = ({ onNext, onPrevious, onSave, savedData = {} }) => {
  const [preferences, setPreferences] = useState({
    workEnvironment: savedData?.workEnvironment || '',
    workStyle: savedData?.workStyle || '',
    teamSize: savedData?.teamSize || '',
    managementStyle: savedData?.managementStyle || '',
    workSchedule: savedData?.workSchedule || '',
    travelWillingness: savedData?.travelWillingness || '',
    salaryExpectation: savedData?.salaryExpectation || '',
    benefits: savedData?.benefits || [],
    industryPreferences: savedData?.industryPreferences || [],
    companySize: savedData?.companySize || '',
    ...savedData
  });

  const workEnvironmentOptions = [
    { value: 'remote', label: 'Fully Remote' },
    { value: 'hybrid', label: 'Hybrid (2-3 days office)' },
    { value: 'office', label: 'Full-time Office' },
    { value: 'flexible', label: 'Flexible Arrangement' }
  ];

  const workStyleOptions = [
    { value: 'independent', label: 'Independent Work' },
    { value: 'collaborative', label: 'Collaborative Team Work' },
    { value: 'mixed', label: 'Mix of Both' }
  ];

  const teamSizeOptions = [
    { value: 'small', label: 'Small Team (2-5 people)' },
    { value: 'medium', label: 'Medium Team (6-15 people)' },
    { value: 'large', label: 'Large Team (16+ people)' },
    { value: 'no_preference', label: 'No Preference' }
  ];

  const managementStyleOptions = [
    { value: 'hands_off', label: 'Hands-off / Autonomous' },
    { value: 'supportive', label: 'Supportive & Guidance' },
    { value: 'structured', label: 'Structured & Clear Direction' },
    { value: 'collaborative', label: 'Collaborative Decision Making' }
  ];

  const workScheduleOptions = [
    { value: 'standard', label: 'Standard Hours (9-5)' },
    { value: 'flexible', label: 'Flexible Hours' },
    { value: 'early', label: 'Early Hours (7-3)' },
    { value: 'late', label: 'Late Hours (11-7)' }
  ];

  const travelOptions = [
    { value: 'none', label: 'No Travel' },
    { value: 'minimal', label: 'Minimal (1-2 times/year)' },
    { value: 'occasional', label: 'Occasional (Monthly)' },
    { value: 'frequent', label: 'Frequent (Weekly)' }
  ];

  const salaryRanges = [
    { value: '30-50k', label: '$30,000 - $50,000' },
    { value: '50-75k', label: '$50,000 - $75,000' },
    { value: '75-100k', label: '$75,000 - $100,000' },
    { value: '100-150k', label: '$100,000 - $150,000' },
    { value: '150k+', label: '$150,000+' }
  ];

  const companySizeOptions = [
    { value: 'startup', label: 'Startup (1-50 employees)' },
    { value: 'small', label: 'Small Company (51-200 employees)' },
    { value: 'medium', label: 'Medium Company (201-1000 employees)' },
    { value: 'large', label: 'Large Company (1000+ employees)' }
  ];

  const benefitOptions = [
    { id: 'health_insurance', label: 'Health Insurance', icon: 'Heart' },
    { id: 'dental_vision', label: 'Dental & Vision', icon: 'Eye' },
    { id: 'retirement_401k', label: '401(k) / Retirement Plan', icon: 'PiggyBank' },
    { id: 'paid_time_off', label: 'Generous PTO', icon: 'Calendar' },
    { id: 'flexible_schedule', label: 'Flexible Schedule', icon: 'Clock' },
    { id: 'professional_development', label: 'Professional Development', icon: 'BookOpen' },
    { id: 'stock_options', label: 'Stock Options/Equity', icon: 'TrendingUp' },
    { id: 'bonus_structure', label: 'Performance Bonuses', icon: 'Award' }
  ];

  const industryOptions = [
    { id: 'technology', label: 'Technology', icon: 'Laptop' },
    { id: 'healthcare', label: 'Healthcare', icon: 'Heart' },
    { id: 'finance', label: 'Finance & Banking', icon: 'DollarSign' },
    { id: 'education', label: 'Education', icon: 'GraduationCap' },
    { id: 'retail', label: 'Retail & E-commerce', icon: 'ShoppingCart' },
    { id: 'manufacturing', label: 'Manufacturing', icon: 'Settings' },
    { id: 'consulting', label: 'Consulting', icon: 'Users' },
    { id: 'media', label: 'Media & Entertainment', icon: 'Video' }
  ];

  const updatePreference = (key, value) => {
    setPreferences(prev => ({ ...prev, [key]: value }));
  };

  const toggleBenefit = (benefitId) => {
    setPreferences(prev => ({
      ...prev,
      benefits: prev?.benefits?.includes(benefitId)
        ? prev?.benefits?.filter(id => id !== benefitId)
        : [...prev?.benefits, benefitId]
    }));
  };

  const toggleIndustry = (industryId) => {
    setPreferences(prev => ({
      ...prev,
      industryPreferences: prev?.industryPreferences?.includes(industryId)
        ? prev?.industryPreferences?.filter(id => id !== industryId)
        : [...prev?.industryPreferences, industryId]
    }));
  };

  const handleNext = () => {
    onSave(preferences);
    onNext();
  };

  const isFormValid = () => {
    return preferences?.workEnvironment && 
           preferences?.workStyle && 
           preferences?.salaryExpectation &&
           preferences?.benefits?.length > 0;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-text-primary mb-2">Work Preferences</h3>
        <p className="text-text-secondary">
          Tell us about your ideal work environment and preferences to help us find the perfect job matches.
        </p>
      </div>
      <div className="space-y-6">
        {/* Work Environment */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Select
            label="Work Environment"
            description="How do you prefer to work?"
            options={workEnvironmentOptions}
            value={preferences?.workEnvironment}
            onChange={(value) => updatePreference('workEnvironment', value)}
            placeholder="Select work environment"
            required
          />

          <Select
            label="Work Style"
            description="What's your preferred working style?"
            options={workStyleOptions}
            value={preferences?.workStyle}
            onChange={(value) => updatePreference('workStyle', value)}
            placeholder="Select work style"
            required
          />
        </div>

        {/* Team and Management */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Select
            label="Team Size Preference"
            description="What team size do you work best in?"
            options={teamSizeOptions}
            value={preferences?.teamSize}
            onChange={(value) => updatePreference('teamSize', value)}
            placeholder="Select team size"
          />

          <Select
            label="Management Style"
            description="What management style do you prefer?"
            options={managementStyleOptions}
            value={preferences?.managementStyle}
            onChange={(value) => updatePreference('managementStyle', value)}
            placeholder="Select management style"
          />
        </div>

        {/* Schedule and Travel */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Select
            label="Work Schedule"
            description="What's your preferred work schedule?"
            options={workScheduleOptions}
            value={preferences?.workSchedule}
            onChange={(value) => updatePreference('workSchedule', value)}
            placeholder="Select schedule preference"
          />

          <Select
            label="Travel Willingness"
            description="How much are you willing to travel?"
            options={travelOptions}
            value={preferences?.travelWillingness}
            onChange={(value) => updatePreference('travelWillingness', value)}
            placeholder="Select travel preference"
          />
        </div>

        {/* Salary and Company Size */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Select
            label="Salary Expectation"
            description="What's your expected salary range?"
            options={salaryRanges}
            value={preferences?.salaryExpectation}
            onChange={(value) => updatePreference('salaryExpectation', value)}
            placeholder="Select salary range"
            required
          />

          <Select
            label="Company Size"
            description="What size company do you prefer?"
            options={companySizeOptions}
            value={preferences?.companySize}
            onChange={(value) => updatePreference('companySize', value)}
            placeholder="Select company size"
          />
        </div>

        {/* Benefits */}
        <div>
          <h4 className="text-lg font-medium text-text-primary mb-2">Important Benefits</h4>
          <p className="text-sm text-text-secondary mb-4">Select the benefits that matter most to you</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {benefitOptions?.map((benefit) => (
              <button
                key={benefit?.id}
                onClick={() => toggleBenefit(benefit?.id)}
                className={`p-3 border rounded-lg text-left spring-transition ${
                  preferences?.benefits?.includes(benefit?.id)
                    ? 'border-primary bg-primary/10 text-primary' :'border-border hover:border-primary/50 hover:bg-primary/5'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <Icon 
                    name={benefit?.icon} 
                    size={20} 
                    className={preferences?.benefits?.includes(benefit?.id) ? 'text-primary' : 'text-text-secondary'}
                  />
                  <span className="font-medium">{benefit?.label}</span>
                  {preferences?.benefits?.includes(benefit?.id) && (
                    <Icon name="Check" size={16} className="text-primary ml-auto" />
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Industry Preferences */}
        <div>
          <h4 className="text-lg font-medium text-text-primary mb-2">Industry Preferences</h4>
          <p className="text-sm text-text-secondary mb-4">Select industries you're interested in (optional)</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {industryOptions?.map((industry) => (
              <button
                key={industry?.id}
                onClick={() => toggleIndustry(industry?.id)}
                className={`p-3 border rounded-lg text-left spring-transition ${
                  preferences?.industryPreferences?.includes(industry?.id)
                    ? 'border-primary bg-primary/10 text-primary' :'border-border hover:border-primary/50 hover:bg-primary/5'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <Icon 
                    name={industry?.icon} 
                    size={20} 
                    className={preferences?.industryPreferences?.includes(industry?.id) ? 'text-primary' : 'text-text-secondary'}
                  />
                  <span className="font-medium">{industry?.label}</span>
                  {preferences?.industryPreferences?.includes(industry?.id) && (
                    <Icon name="Check" size={16} className="text-primary ml-auto" />
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Preference Summary */}
        {isFormValid() && (
          <div className="p-4 bg-success/10 border border-success/20 rounded-lg">
            <div className="flex items-start space-x-3">
              <Icon name="CheckCircle" size={20} className="text-success flex-shrink-0 mt-0.5" />
              <div>
                <h5 className="font-medium text-text-primary mb-2">Preferences Summary</h5>
                <div className="text-sm text-text-secondary space-y-1">
                  <p>• Work Environment: {workEnvironmentOptions?.find(opt => opt?.value === preferences?.workEnvironment)?.label}</p>
                  <p>• Work Style: {workStyleOptions?.find(opt => opt?.value === preferences?.workStyle)?.label}</p>
                  <p>• Salary Range: {salaryRanges?.find(opt => opt?.value === preferences?.salaryExpectation)?.label}</p>
                  <p>• Selected {preferences?.benefits?.length} important benefits</p>
                  {preferences?.industryPreferences?.length > 0 && (
                    <p>• Interested in {preferences?.industryPreferences?.length} industries</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Navigation */}
      <div className="flex justify-between pt-6 border-t border-border mt-6">
        <Button variant="outline" onClick={onPrevious} iconName="ArrowLeft">
          Previous
        </Button>
        <Button
          onClick={handleNext}
          disabled={!isFormValid()}
          iconName="CheckCircle"
          iconPosition="right"
        >
          Complete Assessment
        </Button>
      </div>
    </div>
  );
};

export default WorkPreferences;